import React from 'react'
import * as UI from 'ui'
import { Translation, Translator, useTranslation } from '@morningtrain/react-translations'
import Widget from 'widgets/Widget'
import { Model, ValueToCollection, WhenModel } from '@morningtrain/react-resources'
import { Table } from '../../ui'
import * as Columns from 'support/columns'
import * as Displays from 'support/displays'
import DoubleColumn from './partials/DoubleColumn'
import { TabbedThemeSwitcher, Theme, WhenTheme } from 'widgets/theme-switcher'
import KpiRangeValue from 'widgets/economy/partials/KpiRangeValue'
import CookieFilter from "support/filters/CookieFilter";

export default function TotalOverviewWidget () {
  const transOverview = useTranslation('economy.total_overview.tabs.overview')
  const transDetailed = useTranslation('economy.total_overview.tabs.detailed')

  return (
    <Widget>
      <Model resourceName='economy' operationName='overview'>
        <CookieFilter name='period' cookieKey='page.filters.period' />
        <Theme themes={['overview', 'detailed']}>
          <Translator namespace='economy.total_overview'>
            <WhenModel>
              <UI.Card tight className='pt-6 sm:pt-10'>

                <UI.CardHeader withPadding>
                  <UI.WithSubtitle subtitle={(
                    <UI.Text uppercase size='xs'>
                      <Displays.Text name='meta.date' />
                    </UI.Text>
                  )}
                  >
                    <UI.Text weight='medium' size='lg'>
                      <Translation name='title' />
                    </UI.Text>
                  </UI.WithSubtitle>
                  <UI.ButtonWrap>
                    <TabbedThemeSwitcher options={[{ value: 'overview', label: transOverview }, { value: 'detailed', label: transDetailed }]} />
                  </UI.ButtonWrap>
                </UI.CardHeader>

                <Translator namespace='economy.total_overview.columns'>

                  <ValueToCollection name='collection'>

                    <Table withFooter striped>
                      <Columns.Text
                        name='name'
                        label={(
                          <UI.WithSubtitle subtitle={<UI.Text size='xs'><Translation name='generic.name' /></UI.Text>}>
                            <Translation name='generic.department' />
                          </UI.WithSubtitle>
                        )}
                        footer={<Translation name='generic.total' />}
                      />
                      <Columns.CompactNumber
                        name='revenue'
                        alignment='right'
                        minDecimals={0}
                        aggregate='sum'
                        label={(
                          <UI.WithSubtitle subtitle={<UI.Text size='xs'><Translation name='generic.in_period' /></UI.Text>}>
                            <Translation name='generic.revenue' />
                          </UI.WithSubtitle>
                        )}
                      />
                      <WhenTheme type='overview'>

                        <Columns.Column
                          alignment='right'
                          label={(
                            <UI.WithSubtitle subtitle={<UI.Text size='xs'>LY / Budget</UI.Text>}>
                              <Translation name='generic.comparison' />
                            </UI.WithSubtitle>
                            )}
                          display={
                            <DoubleColumn
                              firstDisplay={(
                                <Displays.Percentage
                                  name='revenue_change'
                                  decimals={0}
                                  badBelow={0}
                                  positiveColor='green'
                                />
                              )}
                              secondDisplay={(
                                <Displays.Percentage
                                  name='budget_comparison'
                                  decimals={0}
                                  badBelow={0}
                                  positiveColor='green'
                                />
                              )}
                            />
                          }
                          footer={(
                            <DoubleColumn
                              firstDisplay={(
                                <Displays.AggregateChange
                                  name='revenue'
                                  outOfName='revenue_ly'
                                  display={(
                                    <Displays.Percentage
                                      decimals={0}
                                      badBelow={0}
                                      positiveColor='green'
                                    />
                                  )}
                                />
                              )}
                              secondDisplay={(
                                <Displays.AggregateChange
                                  name='revenue'
                                  outOfName='revenue_budget'
                                  display={(
                                    <Displays.Percentage
                                      decimals={0}
                                      badBelow={0}
                                      positiveColor='green'
                                    />
                                  )}
                                />
                              )}
                            />
                          )}
                        />

                        <Columns.Column
                          alignment='right'
                          name='takeaway'
                          label={(
                            <UI.WithSubtitle subtitle={(
                              <UI.Text size='xs'>
                                <Translation name='generic.revenue' /> / <Translation name='generic.share' />
                              </UI.Text>
                            )}
                            >
                              takeaway
                            </UI.WithSubtitle>
                          )}
                          display={(
                            <DoubleColumn
                              firstDisplay={<Displays.CompactNumber name='takeaway_revenue' decimals={0} minDecimals={0} />}
                              secondDisplay={<Displays.Percentage name='takeaway_share' decimals={0} />}
                            />
                          )}
                          footer={(
                            <DoubleColumn
                              firstDisplay={(
                                <Displays.Aggregate
                                  name='takeaway_revenue'
                                  display={(
                                    <Displays.CompactNumber decimals={0} minDecimals={0} />
                                  )}
                                />
                              )}
                              secondDisplay={(
                                <Displays.AggregateRatio
                                  name='takeaway_revenue'
                                  outOfName='revenue'
                                  display={(
                                    <Displays.Percentage decimals={0} />
                                  )}
                                />
                              )}
                            />
                          )}
                        />
                        <Columns.Number
                          name='average_basket_size'
                          label={(
                            <UI.WithSubtitle subtitle={<UI.Text size='xs'><Translation name='generic.in_period' /></UI.Text>}>
                              <Translation name='generic.average_basket_size' />
                            </UI.WithSubtitle>
                          )}
                          alignment='right'
                          decimals={0}
                          footer={<Displays.Number name='average_basket_size' decimals={0}/>}
                        />
                        <Columns.Number
                          name='guest_count'
                          label={(
                            <UI.WithSubtitle subtitle={<UI.Text size='xs'><Translation name='generic.in_period' /></UI.Text>}>
                              <Translation name='generic.guest_count' />
                            </UI.WithSubtitle>
                          )}
                          alignment='right'
                          decimals={0}
                          aggregate='sum'
                        />
                        <Columns.WageRatio
                          name='wage_ratio'
                          alignment='right'
                          label={(
                            <UI.WithSubtitle subtitle={<UI.Text size='xs'><Translation name='generic.in_period' /></UI.Text>}>
                              <Translation name='generic.wage_ratio' />
                            </UI.WithSubtitle>
                          )}
                          footer={(
                            <Displays.AggregateRatio
                              name='wage_ratio_salary'
                              outOfName='wage_ratio_revenue'
                              display={(
                                <Displays.WageRatio />
                              )}
                            />
                          )}
                        />
                        <Columns.Percentage
                          name='checklist'
                          alignment='right'
                          decimals={0}
                          label={(
                            <UI.WithSubtitle subtitle={<UI.Text size='xs'><Translation name='generic.completed' /></UI.Text>}>
                              <Translation name='columns.checklists.label' />
                            </UI.WithSubtitle>
                          )}
                          footer={(
                            <Displays.AggregateRatio
                              name='checklists_filled_count'
                              outOfName='checklists_total_count'
                              display={(
                                <Displays.Percentage
                                  decimals={0}
                                />
                              )}
                            />
                          )}
                        />
                      </WhenTheme>
                      <WhenTheme type='detailed'>

                        <Columns.Number
                          name='guest_count'
                          label={(
                            <UI.WithSubtitle subtitle={<UI.Text size='xs'><Translation name='generic.in_period' /></UI.Text>}>
                              <Translation name='generic.guest_count' />
                            </UI.WithSubtitle>
                          )}
                          alignment='right'
                          decimals={0}
                          aggregate='sum'
                        />

                        <Columns.Column
                          alignment='right'
                          label={(
                            <UI.WithSubtitle subtitle={(
                              <UI.Text size='xs'>
                                <Translation name='generic.inhouse' />
                                <span> / </span>
                                <Translation name='generic.takeaway' />
                              </UI.Text>
                            )}
                            >
                              <Translation name='columns.beverages_per_guest.label' />
                            </UI.WithSubtitle>
                          )}
                          footer={(
                            <DoubleColumn
                              firstDisplay={(
                                <KpiRangeValue
                                  name='drinks_inhouse_ratio'
                                  lowerLimitName='indicators.beverages_per_guest_restaurant_lower_limit'
                                  upperLimitName='indicators.beverages_per_guest_restaurant_upper_limit'
                                >
                                  <UI.Text color='black'>
                                    <Displays.Number name='drinks_inhouse_ratio' />
                                  </UI.Text>
                                </KpiRangeValue>
                            )}
                              secondDisplay={(
                                <KpiRangeValue
                                  name='drinks_takeaway_ratio'
                                  lowerLimitName='indicators.beverages_per_guest_takeaway_lower_limit'
                                  upperLimitName='indicators.beverages_per_guest_takeaway_upper_limit'
                                >
                                  <UI.Text color='black'>
                                    <Displays.Number name='drinks_takeaway_ratio' />
                                  </UI.Text>
                                </KpiRangeValue>
                            )}
                            />
                        )}
                        >
                          <DoubleColumn
                            firstDisplay={(
                              <KpiRangeValue
                                name='drinks_inhouse_ratio'
                                lowerLimitName='indicators.beverages_per_guest_restaurant_lower_limit'
                                upperLimitName='indicators.beverages_per_guest_restaurant_upper_limit'
                              >
                                <UI.Text color='black'>
                                  <Displays.Number name='drinks_inhouse_ratio' />
                                </UI.Text>
                              </KpiRangeValue>
                          )}
                            secondDisplay={(
                              <KpiRangeValue
                                name='drinks_takeaway_ratio'
                                lowerLimitName='indicators.beverages_per_guest_takeaway_lower_limit'
                                upperLimitName='indicators.beverages_per_guest_takeaway_upper_limit'
                              >
                                <UI.Text color='black'>
                                  <Displays.Number name='drinks_takeaway_ratio' />
                                </UI.Text>
                              </KpiRangeValue>
                          )}
                          />
                        </Columns.Column>

                        <Columns.Column
                          alignment='right'
                          label={(
                            <UI.WithSubtitle subtitle={(
                              <UI.Text size='xs'>
                                <Translation name='generic.inhouse' />
                                <span> / </span>
                                <Translation name='generic.takeaway' />
                              </UI.Text>
                            )}
                            >
                              <Translation name='columns.food_items_per_guest.label' />
                            </UI.WithSubtitle>
                          )}
                          footer={(
                            <DoubleColumn
                              firstDisplay={(
                                <KpiRangeValue
                                  name='food_inhouse_ratio'
                                  lowerLimitName='indicators.items_per_guest_restaurant_lower_limit'
                                  upperLimitName='indicators.items_per_guest_restaurant_upper_limit'
                                >
                                  <UI.Text color='black'>
                                    <Displays.Number name='food_inhouse_ratio' />
                                  </UI.Text>
                                </KpiRangeValue>
                            )}
                              secondDisplay={(
                                <KpiRangeValue
                                  name='food_takeaway_ratio'
                                  lowerLimitName='indicators.items_per_guest_takeaway_lower_limit'
                                  upperLimitName='indicators.items_per_guest_takeaway_upper_limit'
                                >
                                  <UI.Text color='black'>
                                    <Displays.Number name='food_takeaway_ratio' />
                                  </UI.Text>
                                </KpiRangeValue>
                            )}
                            />
                        )}
                        >
                          <DoubleColumn
                            firstDisplay={(
                              <KpiRangeValue
                                name='food_inhouse_ratio'
                                lowerLimitName='indicators.items_per_guest_restaurant_lower_limit'
                                upperLimitName='indicators.items_per_guest_restaurant_upper_limit'
                              >
                                <UI.Text color='black'>
                                  <Displays.Number name='food_inhouse_ratio' />
                                </UI.Text>
                              </KpiRangeValue>
                          )}
                            secondDisplay={(
                              <KpiRangeValue
                                name='food_takeaway_ratio'
                                lowerLimitName='indicators.items_per_guest_takeaway_lower_limit'
                                upperLimitName='indicators.items_per_guest_takeaway_upper_limit'
                              >
                                <UI.Text color='black'>
                                  <Displays.Number name='food_takeaway_ratio' />
                                </UI.Text>
                              </KpiRangeValue>
                          )}
                          />
                        </Columns.Column>

                        <Columns.HumanizedDuration
                          name='average_seniority_in_days'
                          alignment='right'
                          label={(
                            <UI.WithSubtitle subtitle={<UI.Text size='xs'><Translation name='generic.current_average' /></UI.Text>}>
                              <Translation name='generic.seniority' />
                            </UI.WithSubtitle>
                          )}
                          aggregate='avg'
                        />

                      </WhenTheme>
                    </Table>
                  </ValueToCollection>
                </Translator>
              </UI.Card>
            </WhenModel>
          </Translator>
        </Theme>
      </Model>
    </Widget>
  )
}
